import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { DatePicker, Select, TextInput } from "components/inputs";
import type { FieldErrors } from "react-hook-form";
import KaboodleModal from "components/Modals/KaboodleModal";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGetClientByIdQuery } from "services/client-api";
import { useCreateSection111InvoiceMutation } from "services/invoice-api";
import { useGetRreByIdQuery } from "services/rre-api";
import { useConstants } from "utils/hooks";

type Section111InvoiceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  detailIds?: number[];
  selectAllFilters?: {
    expected_count: number;
    date_from?: string;
    date_to?: string;
    hide_invoiced?: boolean;
    nsa_filter?: string;
    action_filter?: string;
    doi_from?: string;
    doi_to?: string;
    tpoc_from?: string;
    tpoc_to?: string;
    medicare_from?: string;
    medicare_to?: string;
  };
  totalCount: number;
  fileType: "C" | "Q";
  rreId: number;
  hasAlreadyInvoiced: boolean;
  onSuccess: () => void;
};

const TERMS_OPTIONS: OptionType[] = [
  { label: "Due on Receipt", value: "Due on Receipt" },
  { label: "Net 15", value: "Net 15" },
  { label: "Net 30", value: "Net 30" },
  { label: "Net 60", value: "Net 60" },
  { label: "Net 90", value: "Net 90" },
];

const BATCHING_OPTIONS: OptionType[] = [
  { label: "Single Invoice", value: "single_invoice" },
  { label: "Per Detail", value: "per_file" },
];

const CLAIM_FEE_TYPE_OPTIONS: OptionType[] = [
  { label: "Flat Per First Report", value: "flat_per_first_report" },
  { label: "Per Report", value: "per_report" },
  { label: "Custom Hourly", value: "custom_hourly" },
];

const QUERY_FEE_TYPE_OPTIONS: OptionType[] = [
  { label: "Per Query Send", value: "per_query_send" },
  { label: "Custom Hourly", value: "custom_hourly" },
];

const DEFAULT_RATES: Record<string, string> = {
  flat_per_first_report: "500.00",
  per_report: "30.00",
  per_query_send: "7.00",
};

type CustomLineItem = {
  description: string;
  quantity: string;
  rate: string;
  date: string;
};

const Section111InvoiceModal: React.FC<Section111InvoiceModalProps> = ({
  isOpen,
  onClose,
  detailIds,
  selectAllFilters,
  totalCount,
  fileType,
  rreId,
  hasAlreadyInvoiced,
  onSuccess,
}) => {
  const { constants } = useConstants();
  const [createInvoice, { isLoading }] = useCreateSection111InvoiceMutation();

  const { data: rreData } = useGetRreByIdQuery({ rreId }, { skip: !rreId });
  const clientId = rreData?.rre_profile?.client_id;

  const { data: clientData } = useGetClientByIdQuery(
    { id: clientId! },
    { skip: !clientId },
  );

  const [selectedAssignee, setSelectedAssignee] = useState<OptionType | null>(
    null,
  );
  const [feeType, setFeeType] = useState<OptionType | null>(null);
  const [rate, setRate] = useState("");
  const [batchingMode, setBatchingMode] = useState<OptionType>(
    BATCHING_OPTIONS[0],
  );
  const [emailSubject, setEmailSubject] = useState("");
  const [description, setDescription] = useState("");
  const [terms, setTerms] = useState<OptionType>(TERMS_OPTIONS[0]);
  const [billingEmail, setBillingEmail] = useState("");
  const [forceOverride, setForceOverride] = useState(false);
  const [customLineItems, setCustomLineItems] = useState<CustomLineItem[]>([
    { description: "", quantity: "1.00", rate: "", date: "" },
  ]);
  const [formErrors, setFormErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (clientData && !billingEmail) {
      setBillingEmail(clientData.billing_email || clientData.email || "");
    }
  }, [clientData]);

  const feeTypeOptions =
    fileType === "C" ? CLAIM_FEE_TYPE_OPTIONS : QUERY_FEE_TYPE_OPTIONS;

  const userOptions = useMemo(() => {
    return (
      constants?.USERS?.map((user) => ({
        label: user.label,
        value: user.value,
      })) || []
    );
  }, [constants?.USERS]);

  const isCustomHourly = feeType?.value === "custom_hourly";

  const handleFeeTypeChange = useCallback((option: OptionType | null) => {
    setFeeType(option);
    if (option && option.value !== "custom_hourly") {
      setRate(DEFAULT_RATES[String(option.value)] || "");
    }
  }, []);

  const addLineItem = () => {
    setCustomLineItems([
      ...customLineItems,
      { description: "", quantity: "1.00", rate: "", date: "" },
    ]);
  };

  const removeLineItem = (index: number) => {
    setCustomLineItems(customLineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (
    index: number,
    field: keyof CustomLineItem,
    value: string,
  ) => {
    const updated = [...customLineItems];
    updated[index] = { ...updated[index], [field]: value };
    setCustomLineItems(updated);
  };

  const handleSubmit = async () => {
    const errs: FieldErrors = {};

    if (!selectedAssignee) {
      errs.assignee = { type: "required", message: "Assignee is required" };
    }
    if (!feeType) {
      errs.fee_type = { type: "required", message: "Fee type is required" };
    }
    if (!isCustomHourly && !rate) {
      errs.rate = { type: "required", message: "Rate is required" };
    }
    if (!emailSubject.trim()) {
      errs.email_subject = {
        type: "required",
        message: "Client subject is required",
      };
    }
    if (!billingEmail.trim()) {
      errs.billing_email = {
        type: "required",
        message: "Billing email is required",
      };
    }

    if (isCustomHourly) {
      const validItems = customLineItems.filter(
        (item) => item.description.trim() && item.rate,
      );
      if (validItems.length === 0) {
        errs.line_items = {
          type: "required",
          message: "At least one line item is required",
        };
      }
    }

    setFormErrors(errs);

    if (Object.keys(errs).length > 0) {
      return;
    }

    const qbWorkspaceId = clientData?.quickbooks_workspace;
    if (!qbWorkspaceId) {
      KaboodleToaster.error(
        "This client does not have a QuickBooks workspace configured",
      );
      return;
    }

    let feeConfig: Record<string, unknown>;
    if (isCustomHourly) {
      const validItems = customLineItems.filter(
        (item) => item.description.trim() && item.rate,
      );
      feeConfig = {
        type: "custom_hourly",
        line_items: validItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          date: item.date || null,
        })),
      };
    } else {
      feeConfig = {
        type: String(feeType!.value),
        rate,
      };
    }

    try {
      const result = await createInvoice({
        rre_id: rreId,
        file_type: fileType,
        ...(selectAllFilters
          ? { select_all_filters: selectAllFilters }
          : { detail_ids: detailIds }),
        client_id: clientId,
        quickbooks_workspace_id: qbWorkspaceId,
        assignee_id: Number(selectedAssignee.value),
        fee_config: feeConfig,
        batching_mode: String(batchingMode.value),
        email_subject: emailSubject,
        description: description || undefined,
        terms: String(terms.value),
        billing_email_address: billingEmail || undefined,
        force_override: forceOverride,
      }).unwrap();

      KaboodleToaster.success(
        `Created ${result.total_invoices_created} draft invoice${result.total_invoices_created === 1 ? "" : "s"}`,
      );
      onSuccess();
    } catch (error: unknown) {
      const err = error as {
        data?: Record<string, unknown>;
        status?: number;
      };
      const data = err?.data;

      if (data?.already_invoiced) {
        const ids = data.already_invoiced as number[];
        KaboodleToaster.error(
          `${ids.length} details are already invoiced. Enable "Force Override" to proceed.`,
        );
      } else if (Array.isArray(data)) {
        // DRF non-field errors as array
        KaboodleToaster.error(data.join(". "));
      } else if (data?.non_field_errors) {
        KaboodleToaster.error((data.non_field_errors as string[]).join(". "));
      } else if (data?.detail) {
        KaboodleToaster.error(String(data.detail));
      } else if (data?.message) {
        KaboodleToaster.error(String(data.message));
      } else if (data && typeof data === "object") {
        // Field-level errors: { field_name: ["error msg"] }
        const fieldErrors = Object.entries(data)
          .filter(([, v]) => Array.isArray(v))
          .map(([k, v]) => `${k}: ${(v as string[]).join(", ")}`)
          .join(". ");
        KaboodleToaster.error(fieldErrors || "Failed to create invoice");
      } else {
        KaboodleToaster.error("Failed to create invoice");
      }
    }
  };

  const footer = (
    <>
      <Button onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button onClick={handleSubmit} disabled={isLoading}>
        {isLoading ? "Creating..." : "Create Invoice"}
      </Button>
    </>
  );

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={footer}
      size="large"
    >
      <Card
        heading={`Create Section 111 Invoice — ${totalCount.toLocaleString()} Detail${totalCount === 1 ? "" : "s"}`}
      >
        <TextInput
          name="client"
          label="Client"
          value={
            clientData
              ? `${clientData.client_id ? `${clientData.client_id} - ` : ""}${clientData.name}`
              : "Loading..."
          }
          readOnly
        />

        <Select
          name="assignee"
          label="Assignee"
          options={userOptions}
          value={selectedAssignee}
          onChange={(option) => {
            setSelectedAssignee(option as OptionType | null);
            setFormErrors((prev) => {
              const next = { ...prev };
              delete next.assignee;
              return next;
            });
          }}
          placeholder="Select..."
          errors={formErrors}
          required
        />

        <Select
          name="fee_type"
          label="Fee Type"
          options={feeTypeOptions}
          value={feeType}
          onChange={(option) => {
            handleFeeTypeChange(option as OptionType | null);
            setFormErrors((prev) => {
              const next = { ...prev };
              delete next.fee_type;
              return next;
            });
          }}
          placeholder="Select fee type..."
          errors={formErrors}
          required
        />

        {!isCustomHourly && (
          <TextInput
            name="rate"
            label="Rate"
            value={rate}
            onChange={(e) => {
              setRate(e.target.value);
              setFormErrors((prev) => {
                const next = { ...prev };
                delete next.rate;
                return next;
              });
            }}
            placeholder="0.00"
            type="number"
            errors={formErrors}
            required
          />
        )}

        {isCustomHourly && (
          <>
            {customLineItems.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
                  gap: "8px",
                  alignItems: "end",
                }}
              >
                <TextInput
                  name={`li_desc_${idx}`}
                  label={idx === 0 ? "Description" : ""}
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) =>
                    updateLineItem(idx, "description", e.target.value)
                  }
                />
                <TextInput
                  name={`li_qty_${idx}`}
                  label={idx === 0 ? "Qty" : ""}
                  placeholder="1.00"
                  value={item.quantity}
                  onChange={(e) =>
                    updateLineItem(idx, "quantity", e.target.value)
                  }
                  type="number"
                />
                <TextInput
                  name={`li_rate_${idx}`}
                  label={idx === 0 ? "Rate" : ""}
                  placeholder="0.00"
                  value={item.rate}
                  onChange={(e) => updateLineItem(idx, "rate", e.target.value)}
                  type="number"
                />
                <DatePicker
                  name={`li_date_${idx}`}
                  label={idx === 0 ? "Date" : ""}
                  initialDate={
                    item.date ? new Date(`${item.date}T00:00:00`) : undefined
                  }
                  onDateChange={(date) => {
                    if (date) {
                      const yyyy = date.getFullYear();
                      const mm = String(date.getMonth() + 1).padStart(2, "0");
                      const dd = String(date.getDate()).padStart(2, "0");
                      updateLineItem(idx, "date", `${yyyy}-${mm}-${dd}`);
                    } else {
                      updateLineItem(idx, "date", "");
                    }
                  }}
                  allowFutureDates
                />
                {customLineItems.length > 1 && (
                  <button
                    onClick={() => removeLineItem(idx)}
                    type="button"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: 18 }}>
                      close
                    </span>
                  </button>
                )}
              </div>
            ))}
            <Button onClick={addLineItem}>+ Add Line Item</Button>
          </>
        )}

        <Select
          name="batching_mode"
          label="Batching Mode"
          options={BATCHING_OPTIONS}
          value={batchingMode}
          onChange={(option) => {
            if (option) setBatchingMode(option as OptionType);
          }}
        />

        <Select
          name="terms"
          label="Terms"
          options={TERMS_OPTIONS}
          value={terms}
          onChange={(option) => {
            if (option) setTerms(option as OptionType);
          }}
        />

        <TextInput
          name="email_subject"
          label="Client Subject"
          value={emailSubject}
          onChange={(e) => {
            setEmailSubject(e.target.value);
            setFormErrors((prev) => {
              const next = { ...prev };
              delete next.email_subject;
              return next;
            });
          }}
          placeholder="Section 111 Invoice - March 2026"
          errors={formErrors}
          required
        />

        <TextInput
          name="description"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
        />

        <TextInput
          name="billing_email"
          label="Billing Email"
          value={billingEmail}
          onChange={(e) => {
            setBillingEmail(e.target.value);
            setFormErrors((prev) => {
              const next = { ...prev };
              delete next.billing_email;
              return next;
            });
          }}
          placeholder="billing@client.com"
          type="email"
          errors={formErrors}
          required
        />

        {hasAlreadyInvoiced && (
          <div>
            <label />
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                color: "#b45309",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={forceOverride}
                onChange={(e) => setForceOverride(e.target.checked)}
              />
              Force override — re-invoice already invoiced details
            </label>
          </div>
        )}
      </Card>
    </KaboodleModal>
  );
};

export default Section111InvoiceModal;
